import { blendColors, normalizeSrgbColorToHex, RuleId } from '@wix/a11y-rules';
import { useOnUnmount, usePreviousState } from '@wix/rules-panel-ui';
import { isNotOptional } from '@wix/seo-common-core';
import Color from 'color';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { unsavedChangedHandlersAtom } from '../../../../atoms/session-atoms';
import { usePanelStateAtom } from '../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../hooks/use-a11y-wizard-adapter';
import { useResetUnsavedChangesHandlers } from '../../../../hooks/use-reset-unsaved-changes-handlers';
import { createColorState } from '../helpers/create-color-state';
import { ContrastRatioLevel } from '../types';
export const useCurrentElement = _ref => {
  var _ruleState$state2, _ruleState$state3;
  let {
    element,
    onChangeElementsColor,
    onClickApply,
    minimalRatio,
    elementIndex,
    pageId,
    isGeneric
  } = _ref;
  const {
    resetUnsavedChangesHandlers
  } = useResetUnsavedChangesHandlers();
  const [updatedElement, setUpdatedElement] = useState(element);
  const setUnsavedChangedHandlers = useSetAtom(unsavedChangedHandlersAtom);
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const colorThemeMap = useMemo(() => a11yWizardAdapter.getColorsThemesMap(), [a11yWizardAdapter]);
  const {
    uiElementId,
    elementId,
    affectingAncestors,
    textColor: initialTextColor
  } = element;
  const {
    getRuleState
  } = usePanelStateAtom();
  const ruleState = getRuleState(pageId, RuleId.COLOR_CONTRAST);
  const [ancestorId, ancestorStyle, ancestorClassName] = useMemo(() => {
    var _ruleState$state;
    return (ruleState == null || (_ruleState$state = ruleState.state) == null || (_ruleState$state = _ruleState$state.changedElements) == null || (_ruleState$state = _ruleState$state.get(uiElementId)) == null || (_ruleState$state = _ruleState$state.affectingAncestors) == null ? void 0 : _ruleState$state.find(_ref2 => {
      let [, {
        bgAlpha
      }] = _ref2;
      return bgAlpha && bgAlpha > 0;
    })) || (affectingAncestors == null ? void 0 : affectingAncestors.find(_ref3 => {
      let [, {
        bgAlpha
      }] = _ref3;
      return bgAlpha && bgAlpha > 0;
    })) || (affectingAncestors == null ? void 0 : affectingAncestors[0]) || ['', {}];
  }, [affectingAncestors, uiElementId, ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.changedElements]);
  const stateBgColor = ruleState == null || (_ruleState$state3 = ruleState.state) == null || (_ruleState$state3 = _ruleState$state3.changedElements) == null || (_ruleState$state3 = _ruleState$state3.get(uiElementId)) == null ? void 0 : _ruleState$state3.bgColor;
  const initialAncestorBgColor = stateBgColor ?? ancestorStyle.bgColor;
  const normalizedTextColor = normalizeSrgbColorToHex(initialTextColor);
  const normalizedAncestorBgColor = normalizeSrgbColorToHex(ancestorStyle.bgColor);
  const normalizedInitialAncestorBgColor = normalizeSrgbColorToHex(initialAncestorBgColor);
  const [currentTextColor, setCurrentTextColor] = useState(new Color(normalizedTextColor));
  const [currentAncestorBgColor, setCurrentAncestorBgColor] = useState(new Color(normalizedAncestorBgColor));
  const previousTextColor = usePreviousState(currentTextColor);
  const previousAncestorBgColor = usePreviousState(currentAncestorBgColor);
  const hasChangedFromPrevious = currentTextColor.hex() !== (previousTextColor == null ? void 0 : previousTextColor.hex()) || currentAncestorBgColor.hex() !== (previousAncestorBgColor == null ? void 0 : previousAncestorBgColor.hex());
  const blendedBgColor = useMemo(() => blendColors((affectingAncestors == null ? void 0 : affectingAncestors.map(_ref4 => {
    let [id, {
      bgColor,
      bgAlpha
    }] = _ref4;
    const color = new Color(id === ancestorId ? currentAncestorBgColor : normalizeSrgbColorToHex(bgColor));
    return isNotOptional(bgAlpha) ? color.alpha(bgAlpha) : color;
  })) ?? []), [affectingAncestors, ancestorId, currentAncestorBgColor]);
  const textColorState = createColorState({
    colorThemeMap,
    compId: elementId,
    currentColor: currentTextColor,
    initialColor: new Color(normalizedTextColor),
    setColor: (color, activeElement) => {
      setCurrentTextColor(color);
      onChangeElementsColor({
        activeElement,
        textElement: {
          className: element.className ?? '',
          color: color.hex(),
          id: elementId
        }
      });
      setUpdatedElement(updateElementWithNewColors({
        textColor: color.hex()
      }));
    }
  });
  const bgColorState = {
    ...createColorState({
      colorThemeMap,
      compId: ancestorId,
      currentColor: currentAncestorBgColor,
      initialColor: new Color(normalizedInitialAncestorBgColor),
      setColor: color => {
        setCurrentAncestorBgColor(color);
        onChangeElementsColor({
          ancestorElement: {
            alpha: ancestorStyle.bgAlpha,
            className: ancestorClassName ?? '',
            color: color.hex(),
            id: ancestorId
          }
        });
        setUpdatedElement(updateElementWithNewColors({
          bgColor: color.hex()
        }));
      }
    }),
    // override the current color with the blended color
    hex: blendedBgColor.hex()
  };
  const updateElementWithNewColors = useCallback(_ref5 => {
    var _updatedElement$affec;
    let {
      textColor,
      bgColor
    } = _ref5;
    const updatedAffectingAncestors = (_updatedElement$affec = updatedElement.affectingAncestors) == null ? void 0 : _updatedElement$affec.map(_ref6 => {
      let [id, ancestor, className] = _ref6;
      return [id, {
        bgAlpha: ancestor.bgAlpha,
        bgColor: id === ancestorId && bgColor ? bgColor : ancestor.bgColor
      }, className];
    });
    return {
      ...updatedElement,
      affectingAncestors: updatedAffectingAncestors,
      bgColor: bgColorState.hex,
      textColor: textColor ?? updatedElement.textColor
    };
  }, [ancestorId, bgColorState.hex, updatedElement]);
  const hasColorsChanged = textColorState.hasChanged || bgColorState.hasChanged;
  const currentContrastRatio = new Color(currentTextColor).contrast(blendedBgColor);
  const currentContrastRatioLevel = currentContrastRatio >= minimalRatio ? ContrastRatioLevel.AA : ContrastRatioLevel.Fail;
  const previousContrastRatioLevel = usePreviousState(currentContrastRatioLevel);
  if ((previousContrastRatioLevel === ContrastRatioLevel.Fail && currentContrastRatioLevel !== ContrastRatioLevel.Fail || previousContrastRatioLevel !== undefined && previousContrastRatioLevel !== ContrastRatioLevel.Fail && currentContrastRatioLevel !== ContrastRatioLevel.Fail && hasChangedFromPrevious) && !isGeneric) {
    setUnsavedChangedHandlers({
      apply: async () => onClickApply({
        bgColorState,
        currentContrastRatio,
        currentContrastRatioLevel,
        element: updatedElement,
        elementIndex,
        textColorState
      }),
      exists: true
    });
  } else if (previousContrastRatioLevel !== ContrastRatioLevel.Fail && currentContrastRatioLevel === ContrastRatioLevel.Fail && !isGeneric) {
    resetUnsavedChangesHandlers();
  }
  const isApplyClickedRef = useRef(false);
  const updateUserColorPalette = colors => {
    const themeColors = new Set(Object.values(colorThemeMap));
    const newColors = [];
    if (textColorState.hasChanged && !themeColors.has(textColorState.hex)) {
      newColors.push(textColorState.hex);
    }
    if (bgColorState.hasChanged && !themeColors.has(bgColorState.hex)) {
      newColors.push(bgColorState.hex);
    }
    return newColors.length > 0 ? [...colors, ...newColors] : colors;
  };

  // used to revert the colors if the user closes the dialog without applying the changes
  useOnUnmount(() => {
    resetUnsavedChangesHandlers();
    if (isApplyClickedRef.current) {
      return;
    }
    if (hasColorsChanged) {
      onChangeElementsColor({
        ancestorElement: bgColorState.hasChanged ? {
          className: ancestorClassName ?? '',
          color: ancestorStyle.bgColor,
          id: ancestorId
        } : undefined,
        textElement: textColorState.hasChanged ? {
          className: element.className ?? '',
          color: element.textColor ?? '',
          id: elementId
        } : undefined
      });
    }
  });
  return {
    bgColorState,
    currentContrastRatio,
    hasColorsChanged,
    isApplyClickedRef,
    textColorState,
    updateUserColorPalette,
    updatedElement
  };
};
//# sourceMappingURL=use-current-element.js.map